/**
 * Who the signed-in person is: their school, role and Cubby operator status.
 * Loaded once after sign-in and shared by the shell and every page.
 *
 * Cubby operators are not school members, so /api/session/me answers
 * no_membership for them. In that case this asks /api/ops/me, and the app
 * runs with only the operations pages.
 *
 * What this decides is what renders. RLS and the routes decide what data
 * anyone gets.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError } from "../lib/api";
import { useSession } from "./SessionProvider";

export interface Me {
  user: { id: string; email: string | null; fullName: string | null };
  org: { id: string; name: string; slug: string; timezone: string } | null;
  role: { key: string; label: string; rank: number; isStaff: boolean } | null;
  platformRole: string | null;
}

interface MeContextValue {
  me: Me;
  isDirector: boolean;
  reload: () => void;
}

const MeContext = createContext<MeContextValue | null>(null);

export const DIRECTOR_RANK = 80;

export function MeProvider({ children }: { children: ReactNode }) {
  const { api, session, signOut } = useSession();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setError(null);

    api<Me>("/api/session/me")
      .then((result) => {
        if (active) setMe(result);
      })
      .catch(async (caught: unknown) => {
        if (!active) return;
        if (caught instanceof ApiError && caught.code === "no_membership") {
          try {
            const ops = await api<{ role: string }>("/api/ops/me");
            if (!active) return;
            setMe({
              user: { id: session?.user.id ?? "", email: session?.user.email ?? null, fullName: null },
              org: null,
              role: null,
              platformRole: ops.role,
            });
            return;
          } catch {
            // Not an operator either; show the membership message below.
          }
        }
        if (!active) return;
        setError(caught instanceof ApiError ? caught.message : "Could not load your account.");
      });

    return () => {
      active = false;
    };
  }, [api, attempt, session?.user.id, session?.user.email]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cream px-6 text-center">
        <p className="max-w-md text-stone-700">{error}</p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="rounded-xl border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-ink hover:bg-stone-50"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="text-sm text-stone-600">Loading your account</p>
      </div>
    );
  }

  return (
    <MeContext.Provider value={{ me, isDirector: (me.role?.rank ?? 0) >= DIRECTOR_RANK, reload }}>
      {children}
    </MeContext.Provider>
  );
}

export function useMe(): MeContextValue {
  const value = useContext(MeContext);
  if (!value) throw new Error("useMe must be used inside MeProvider");
  return value;
}
